import { useState } from 'react';
import { DiagnosticLevel, levelInfo } from '@/data/diagnosticQuestions';
import { Check, Lock, Star, Info, MessageSquare, Target, AlertTriangle, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface LevelLadderProps {
  currentLevel: DiagnosticLevel;
}

const levels: DiagnosticLevel[] = ['transformador', 'estratega', 'integrador', 'implementador', 'explorador', 'iniciador'];

const characteristicIcons = [MessageSquare, Target, AlertTriangle, Trophy];

export default function LevelLadder({ currentLevel }: LevelLadderProps) {
  const [selectedLevel, setSelectedLevel] = useState<DiagnosticLevel | null>(null);
  const currentLevelNumber = levelInfo[currentLevel]?.number ?? 0;

  const getLevelStatus = (level: DiagnosticLevel) => {
    const levelNum = levelInfo[level]?.number ?? 0;
    if (levelNum === currentLevelNumber) return 'current';
    if (levelNum < currentLevelNumber) return 'completed';
    return 'locked';
  };

  const getStatusStyles = (status: 'current' | 'completed' | 'locked') => {
    switch (status) {
      case 'current':
        return {
          container: 'bg-primary/20 border-primary shadow-lg shadow-primary/20 scale-105',
          icon: 'bg-primary text-primary-foreground',
          text: 'text-foreground font-semibold',
          badge: 'bg-primary text-primary-foreground'
        };
      case 'completed':
        return {
          container: 'bg-success/10 border-success/50',
          icon: 'bg-success/20 text-success',
          text: 'text-success',
          badge: 'bg-success/20 text-success'
        };
      case 'locked':
        return {
          container: 'bg-muted/50 border-border/50 opacity-60',
          icon: 'bg-muted text-muted-foreground',
          text: 'text-muted-foreground',
          badge: 'bg-muted text-muted-foreground'
        };
    }
  };

  const selectedInfo = selectedLevel ? levelInfo[selectedLevel] : null;
  const selectedStatus = selectedLevel ? getLevelStatus(selectedLevel) : null;

  return (
    <div className="relative py-4">
      {/* Connection line */}
      <div className="absolute left-[2.25rem] top-8 bottom-8 w-0.5 bg-gradient-to-b from-primary via-accent to-muted" />
      
      <div className="space-y-3">
        {levels.map((level) => {
          const status = getLevelStatus(level);
          const styles = getStatusStyles(status);
          const info = levelInfo[level];
          
          return (
            <div
              key={level}
              onClick={() => setSelectedLevel(level)}
              className={cn(
                'relative flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 cursor-pointer hover:ring-2 hover:ring-primary/30',
                styles.container
              )}
            >
              {/* Level indicator */}
              <div className={cn(
                'relative z-10 flex items-center justify-center w-10 h-10 rounded-full transition-all',
                styles.icon
              )}>
                {status === 'current' && <Star className="w-5 h-5 fill-current" />}
                {status === 'completed' && <Check className="w-5 h-5" />}
                {status === 'locked' && <Lock className="w-4 h-4" />}
              </div>
              
              {/* Level info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={cn('font-medium', styles.text)}>
                    {info.title}
                  </span>
                  <span className={cn(
                    'text-xs px-2 py-0.5 rounded-full',
                    styles.badge
                  )}>
                    Nivel {info.number}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                  {info.description}
                </p>
              </div>

              <Info className="w-4 h-4 text-muted-foreground shrink-0" />

              {/* Current level indicator */}
              {status === 'current' && (
                <div className="absolute -right-1 -top-1">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-primary" />
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLevel} onOpenChange={(open) => !open && setSelectedLevel(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className={cn(
                'flex items-center justify-center w-10 h-10 rounded-full',
                selectedStatus === 'current' ? 'bg-primary text-primary-foreground' :
                selectedStatus === 'completed' ? 'bg-success/20 text-success' :
                'bg-muted text-muted-foreground'
              )}>
                <span className="font-bold text-sm">{selectedInfo?.number}</span>
              </div>
              <div>
                <DialogTitle className="flex items-center gap-2">
                  {selectedInfo?.title}
                  {selectedStatus === 'current' && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">Tu nivel actual</span>
                  )}
                </DialogTitle>
                <DialogDescription>Nivel {selectedInfo?.number} de madurez en IA</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-2">
              <p className="text-sm text-foreground leading-relaxed">
                {selectedInfo?.fullDescription}
              </p>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground">Marcadores evaluativos</h4>
                {selectedInfo?.characteristics.map((char, i) => {
                  const Icon = characteristicIcons[i] || Info;
                  const [label, ...rest] = char.split(': ');
                  return (
                    <div key={i} className="flex gap-3 p-3 rounded-lg bg-muted/50 border border-border/50">
                      <Icon className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <span className="font-medium text-foreground">{label}:</span>{' '}
                        <span className="text-muted-foreground">{rest.join(': ')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
